import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react/no-unescaped-entities */
/* eslint-disable max-lines */
import CloseIcon from '@mui/icons-material/Close';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, } from '@mui/material';
import { addDays, addYears } from 'date-fns';
import { useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { EmployeeApi } from '../api';
import { isBreakpointMobile, useBreakpoint } from '../breakpoint';
import { CategoryChip } from '../categoryChip';
import { checkIfWorkingDay } from '../functions';
import { checkHolidayOverlap } from '../functions/checkHolidayOverlap';
import { getDateWithoutTimeInUtc } from '../functions/getDateWithoutTimeInUtc';
import { getHolidayLength } from '../functions/getHolidayLength';
import { useHoliday } from '../hooks';
import { HolidayActionKind } from '../hooks/reducer/holidayActionKind';
import { ModalActionKind } from '../hooks/reducer/modalActionKind';
import { ReducerContext } from '../hooks/reducer/reducerContext';
import { HolidayBooking } from '../types';
import { CalendarContext } from './calendarContext';
import { HolidayPicker } from './HolidayPicker/HolidayPicker';
import { NewHolidaySummary } from './HolidayPicker/NewHolidaySummary';
import { NewBookingContext } from './NewBookingContext';
import { Typography } from './Typography';
export const NewBookingModal = ({ afterSubmit, OnShowManager, OnShowManagerPrompt, }) => {
    const { holidayDispatch, holidayState, modalState, modalDispatch, userSettingsState } = useContext(ReducerContext);
    const { calendarSummaryValues, onSubmitHolidayBooking, freeDays } = useContext(CalendarContext);
    const { holidayBookings } = holidayState;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [showManagerSettingsModal, setShowManagerSettingsModal] = useState(false);
    const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);
    const [showPastInformation, setShowPastInformation] = useState(false);
    const [showFutureEntitlementYearInformation, setShowFutureEntitlementYearInformation] = useState(false);
    const [showPreviousEntitlementYearInformation, setShowPreviousEntitlementYearInformation,] = useState(false);
    const [isInPreviousEntitlementYear, setIsInPreviousEntitlementYear] = useState(false);
    const [isInFutureEntitlementYear, setIsInFutureEntitlementYear] = useState(false);
    const [error, setError] = useState('');
    const breakpoint = useBreakpoint();
    const isMobile = isBreakpointMobile(breakpoint);
    const tidyUp = useCallback(() => {
        setNewBookingState({
            dateRange: { startDate: null, endDate: null },
            startInAfternoon: false,
            endInMorning: false,
            isManagerApprovalRequired: false,
            deductFromEntitlement: false,
        });
        modalDispatch({ type: ModalActionKind.CloseAddBooking });
    }, [modalDispatch]);
    const tidyUpHandler = useCallback((e, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        tidyUp();
    }, [tidyUp]);
    const handleCancel = useCallback(() => {
        tidyUp();
    }, [tidyUp]);
    const [newBookingState, setNewBookingState] = useState({
        dateRange: { startDate: null, endDate: null },
        startInAfternoon: false,
        endInMorning: false,
        isManagerApprovalRequired: false,
        deductFromEntitlement: false,
    });
    const { dateRange, startInAfternoon, endInMorning, isManagerApprovalRequired, deductFromEntitlement, } = newBookingState;
    const startDate = useMemo(() => dateRange.startDate?.toDate(), [dateRange.startDate]);
    const endDate = useMemo(() => dateRange.endDate?.toDate(), [dateRange.endDate]);
    useEffect(() => {
        const endOfCurrentHolidayYear = new Date(userSettingsState.end);
        const endOfLastHolidayYear = addYears(endOfCurrentHolidayYear, -1);
        const dateFromPreviousEntitlementYearSelected = (endDate ?? startDate) < endOfLastHolidayYear;
        setIsInPreviousEntitlementYear(dateFromPreviousEntitlementYearSelected);
        setShowPastInformation((endDate ?? startDate) < new Date() && !dateFromPreviousEntitlementYearSelected);
        setShowPreviousEntitlementYearInformation(dateFromPreviousEntitlementYearSelected);
    }, [endDate, startDate, userSettingsState.end]);
    const { submitHoliday } = useHoliday(EmployeeApi);
    const submit = useCallback(async () => {
        if (!startDate) {
            setError('Date range not set.');
            return;
        }
        const submittedEndDate = endDate ?? startDate;
        const currentDate = new Date();
        const endOfCurrentHolidayYear = new Date(userSettingsState.holidayYearEnd);
        const endOfLastHolidayYear = addYears(endOfCurrentHolidayYear, -1);
        if (startDate < endOfLastHolidayYear && submittedEndDate > endOfLastHolidayYear) {
            setError("You can't book absences that last through previous and current entitlement year");
            return;
        }
        if (submittedEndDate < currentDate && isManagerApprovalRequired) {
            setError('To send an approval request, your absence must occur in the future');
            return;
        }
        // Check for conflict with other absences
        if (checkHolidayOverlap(holidayBookings, startDate, submittedEndDate, startInAfternoon, endInMorning)) {
            setError('Conflict with other absences!');
            return;
        }
        if (!deductFromEntitlement && isManagerApprovalRequired) {
            setError('To send an approval request, your absence must deduct from your entitlement.');
            return;
        }
        const daysInRange = getHolidayLength(startDate, submittedEndDate, startInAfternoon, endInMorning, userSettingsState.workingDays, freeDays);
        if (daysInRange === 0) {
            setError('The dates you have selected do not draw against your entitlement');
            return;
        }
        for (let i = 0; i <= daysInRange; i++) {
            const holidayDate = addDays(startDate, i);
            if (checkIfWorkingDay(holidayDate, userSettingsState.workingDays) === false) {
                holidayDispatch({ type: HolidayActionKind.AddHoliday, payload: holidayDate });
            }
        }
        if (isInPreviousEntitlementYear === false &&
            !isInFutureEntitlementYear &&
            deductFromEntitlement &&
            calendarSummaryValues &&
            daysInRange > calendarSummaryValues.remaining) {
            setError('Not enough days remaining!');
            return;
        }
        try {
            const bookingForm = new HolidayBooking();
            bookingForm.startDate = getDateWithoutTimeInUtc(startDate);
            bookingForm.endDate = getDateWithoutTimeInUtc(submittedEndDate);
            bookingForm.isStartHalfDay = startInAfternoon;
            bookingForm.isEndHalfDay = endInMorning;
            bookingForm.isManagerApprovalRequired = isManagerApprovalRequired;
            bookingForm.deductFromEntitlement = deductFromEntitlement;
            await submitHoliday(onSubmitHolidayBooking, bookingForm);
            afterSubmit(startDate);
        }
        catch (e) {
            setError('Cannot submit booking.');
        }
        tidyUp();
    }, [
        startDate,
        endDate,
        userSettingsState.holidayYearEnd,
        userSettingsState.workingDays,
        isManagerApprovalRequired,
        isInPreviousEntitlementYear,
        holidayBookings,
        startInAfternoon,
        endInMorning,
        deductFromEntitlement,
        freeDays,
        calendarSummaryValues,
        tidyUp,
        holidayDispatch,
        submitHoliday,
        onSubmitHolidayBooking,
        afterSubmit,
        isInFutureEntitlementYear,
    ]);
    useEffect(() => {
        setShowPastInformation((endDate ?? startDate) < new Date());
        const dateFromFutureEntitlementYearSelected = (endDate ?? startDate) > new Date(userSettingsState.end);
        setShowFutureEntitlementYearInformation(dateFromFutureEntitlementYearSelected);
        setIsInFutureEntitlementYear(dateFromFutureEntitlementYearSelected);
    }, [endDate, startDate, userSettingsState.end]);
    const errorAlertCloseHandler = useCallback(() => setError(''), []);
    useEffect(() => {
        setError('');
    }, [dateRange, endInMorning, startInAfternoon]);
    const openManagerSettingsModal = useCallback(() => {
        setShowManagerSettingsModal(true);
    }, []);
    const managerSettingsModalClosed = useCallback(() => {
        setShowManagerSettingsModal(false);
    }, []);
    if (showManagerSettingsModal) {
        return (_jsx(OnShowManager, { open: showManagerSettingsModal, finish: managerSettingsModalClosed }));
    }
    return (_jsx(NewBookingContext.Provider, { value: {
            ...newBookingState,
            updateState: setNewBookingState,
        }, children: _jsxs(Dialog, { fullWidth: true, sx: {
                '& .MuiDialog-container': {
                    alignItems: 'flex-start',
                },
                overflow_y: 'auto',
            }, PaperProps: {
                sx: {
                    maxWidth: 'sm',
                    textAlign: 'center',
                    alignItems: 'center',
                    justifyContent: 'center',
                    woverflow_x: 'hidden',
                },
            }, fullScreen: isMobile, open: modalState.isAddBookingModalOpen, onClose: tidyUpHandler, "aria-labelledby": "alert-dialog-title", "aria-describedby": "alert-dialog-description", "data-testid": 'new-booking-modal', children: [_jsx(DialogTitle, { id: "alert-dialog-title", sx: { paddingBottom: '0px' }, children: _jsxs(Grid, { container: true, children: [_jsx(Grid, { item: true, xs: 11, children: _jsxs(Typography, { isLoading: false, component: "div", variant: isMobile ? 'body2' : 'body1', children: ["You have", ' ', _jsx(CategoryChip, { categoryName: "remaining", value: calendarSummaryValues?.remaining ?? 0, isLoading: false }), ' ', "days remaining"] }) }), _jsx(Grid, { item: true, xs: 1, justifyContent: 'right', children: _jsx(IconButton, { onClick: handleCancel, sx: { ml: 'auto' }, children: _jsx(CloseIcon, {}) }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(Typography, { isLoading: false, variant: isMobile ? 'body2' : 'body1', children: "Please select your absence" }) })] }) }), _jsxs(DialogContent, { sx: { paddingLeft: '0', paddingRight: '0' }, children: [_jsx(HolidayPicker, {}), _jsx(NewHolidaySummary, { setIsSubmitButtonDisabled: setIsSubmitButtonDisabled, closeParentModal: handleCancel, onOpenManagerSettingsModalClick: openManagerSettingsModal, OnShowManagerPrompt: OnShowManagerPrompt }), error.length > 0 && (_jsx(Alert, { variant: "standard", severity: "error", onClose: errorAlertCloseHandler, children: error }))] }), _jsx(DialogActions, { sx: { width: '90%', borderTop: 'solid 1px', borderColor: 'black' }, children: _jsx(Grid, { container: true, alignItems: "center", justifyContent: "center", children: _jsxs(Grid, { item: true, xs: 12, children: [showPreviousEntitlementYearInformation ? (_jsx(Alert, { severity: "info", sx: { mb: '0.5rem' }, children: "You are booking an absence in a previous entitlement year, this absence won't impact your current year's entitlement." })) : null, showPastInformation ? (_jsx(Alert, { severity: "info", sx: { mb: '0.5rem' }, children: "You are booking a historic absence." })) : null, !showFutureEntitlementYearInformation ? null : (_jsx(Alert, { severity: "info", sx: { mb: '0.5rem' }, children: "You are booking an absence in a future entitlement year, this absence won't impact your current year's entitlement." })), _jsx(Grid, { item: true, children: _jsx(Button, { "aria-label": "Submit", color: "primary", variant: "contained", onClick: submit, disabled: isSubmitButtonDisabled, children: "Submit" }) })] }) }) })] }) }));
};
